import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { format, parseISO } from 'date-fns';
import apiService from '../../services/apiService';
import BillPrintDialog from './BillPrintDialog';

const PrintDayBill = () => {
  const [bills, setBills] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [isBillPrintDialogOpen, setBillPrintDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedBillDetails, setSelectedBillDetails] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchBills = useCallback(async () => {
    try {
      const response = await apiService.collections.getTodayCollections({
        date: selectedDate,
        billTypeFlag: 'P',
      });
      setBills(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching bills:', error);
      setBills([]);
    }
  }, [selectedDate]);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await apiService.customers.getMinDetails();
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  const handlePrint = async (bill) => {
    try {
      const response = await apiService.salesDetails.getSalesByBillSummary(bill.billSummaryId);
      const salesDetails = response.data;

      // Find the full customer details
      const customer = customers.find((c) => c.customerId === bill.customerId);

      // Create a complete bill object with all necessary details for printing
      const billToPrint = {
        ...bill,
        customerName: customer?.customerName || '',
        customerNameTamil: customer?.customerNameTamil || '',
      };

      setSelectedBill(billToPrint);
      setSelectedBillDetails(salesDetails);
      
      // Only open the dialog AFTER the data has been fetched and set
      setBillPrintDialogOpen(true);
    } catch (error) {
      console.error('Error fetching sales details for printing:', error);
      alert('Could not fetch bill details for printing.');
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find((c) => c.customerId === customerId);
    return customer?.customerNameTamil || customer?.customerName || 'N/A';
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, flexGrow: 1, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 24px)' }}>
      <Typography variant={isMobile ? "h6" : "h4"} gutterBottom sx={{ fontWeight: isMobile ? 800 : 400 }}>
        Print Product Bill
      </Typography>

      <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 2, width: isMobile ? '100%' : 'auto', alignSelf: 'flex-start' }}>
        <TextField
          label="Select Date"
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          size="small"
          fullWidth={isMobile}
          sx={{ minWidth: isMobile ? 'auto' : '250px' }}
        />
      </Paper>

      {isMobile ? (
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {bills.map((bill) => {
            const billAmount = parseFloat(bill.billAmount || 0);
            const oldBalance = parseFloat(bill.oldBalance || 0);
            const totalAmount = billAmount + oldBalance;

            return (
              <Card key={bill.billSummaryId} sx={{ mb: 1.5, boxShadow: 2 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, mr: 1 }}>
                      {getCustomerName(bill.customerId)}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        #{bill.manualBillNo}
                      </Typography>
                    </Typography>
                    <IconButton color="primary" onClick={() => handlePrint(bill)} size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <PrintIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                    <Box>
                      <Typography variant="caption" display="block" color="text.secondary">Bill Amt</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{billAmount.toFixed(2)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" display="block" color="text.secondary">Old Bal</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{oldBalance.toFixed(2)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" display="block" color="text.secondary">Total</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>{totalAmount.toFixed(2)}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
          {bills.length === 0 && (
            <Typography variant="body2" align="center" sx={{ mt: 4, color: 'text.secondary' }}>No bills found</Typography>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0 }}>
          <Table stickyHeader>
            <TableHead sx={{ '& .MuiTableCell-head': { backgroundColor: '#e3f2fd', fontWeight: 700 } }}>
            <TableRow>
              <TableCell>S.No</TableCell>
              <TableCell>Bill No</TableCell>
              <TableCell>Customer Name</TableCell>
              <TableCell align="right">Bill Amount</TableCell>
              <TableCell align="right">Old Balance</TableCell>
              <TableCell align="right">Total Amount</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bills.map((bill, index) => {
              const billAmount = parseFloat(bill.billAmount || 0);
              const oldBalance = parseFloat(bill.oldBalance || 0);
              const totalAmount = billAmount + oldBalance;

              return (
                <TableRow key={bill.billSummaryId}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{bill.manualBillNo}</TableCell>
                  <TableCell>{getCustomerName(bill.customerId)}</TableCell>
                  <TableCell align="right">{billAmount.toFixed(2)}</TableCell>
                  <TableCell align="right">{oldBalance.toFixed(2)}</TableCell>
                  <TableCell align="right">{totalAmount.toFixed(2)}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      onClick={() => handlePrint(bill)}
                      startIcon={<PrintIcon />}
                      size="small"
                    >
                      Print
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {selectedBill && (
        <BillPrintDialog
          open={isBillPrintDialogOpen}
          onClose={() => setBillPrintDialogOpen(false)}
          bill={selectedBill}
          salesDetails={selectedBillDetails}
        />
      )}
    </Box>
  );
};

export default PrintDayBill;
